import {
  Building2,
  ShieldCheck,
  Landmark,
  Tags,
  Briefcase,
  Package,
  Hash,
  Percent,
  Ruler,
  CreditCard,
  FileCheck,
  FileText,
  Banknote,
  Stamp,
  Settings,
  LayoutGrid,
  Mail,
  Layers,
  CalendarRange,
  CalendarDays,
  Palette,
  Copy,
  Laptop,
  Activity,
  type LucideIcon,
} from 'lucide-react'

export interface SettingItem {
  id: string
  label: string
  href: string
  icon: LucideIcon
  category: 'My Account' | 'Admin' | 'Assets' | 'Master Data' | 'HR'
  categoryKey: 'my-account' | 'admin' | 'assets' | 'master-data' | 'hr'
  description: string
  iconBg: string
  hoverBorder: string
  badge: string
  /** Overrides the group's access for this one item. */
  access?: SettingAccess
}

/** Who sees a group: everyone, owners and super admins only, or those plus Admins of the HR department. */
export type SettingAccess = 'everyone' | 'superadmin' | 'hr'

export interface SettingGroup {
  key: 'my-account' | 'admin' | 'assets' | 'master-data' | 'hr'
  label: string
  description: string
  access: SettingAccess
  items: SettingItem[]
}

export const MY_ACCOUNT_SECTIONS: SettingItem[] = [
  {
    id: 'email',
    label: 'Email Settings',
    href: '/app/settings/email',
    icon: Mail,
    category: 'My Account',
    categoryKey: 'my-account',
    description: 'Connect your personal Outlook or SMTP mailbox to send quotes, proformas, and invoices directly.',
    iconBg: 'bg-blue-500/10 text-blue-600 dark:bg-blue-400/20 dark:text-blue-400',
    hoverBorder: 'hover:border-blue-500/40 hover:shadow-blue-500/5',
    badge: 'Account',
  },
]

export const ADMIN_SECTIONS: SettingItem[] = [
  {
    id: 'departments',
    label: 'Departments',
    href: '/app/settings/departments',
    icon: Building2,
    category: 'Admin',
    categoryKey: 'admin',
    description: 'Manage organization divisions, teams, department theme colors, and internal hierarchy.',
    iconBg: 'bg-indigo-500/10 text-indigo-600 dark:bg-indigo-400/20 dark:text-indigo-400',
    hoverBorder: 'hover:border-indigo-500/40 hover:shadow-indigo-500/5',
    badge: 'Workspace',
  },
  {
    id: 'roles',
    label: 'Roles & Permissions',
    href: '/app/settings/roles',
    icon: ShieldCheck,
    category: 'Admin',
    categoryKey: 'admin',
    description: 'Configure granular role-based access control, security policies, and team permissions.',
    iconBg: 'bg-purple-500/10 text-purple-600 dark:bg-purple-400/20 dark:text-purple-400',
    hoverBorder: 'hover:border-purple-500/40 hover:shadow-purple-500/5',
    badge: 'Security',
  },
  {
    id: 'company',
    label: 'Company Profile',
    href: '/app/settings/company',
    icon: Landmark,
    category: 'Admin',
    categoryKey: 'admin',
    description: 'Update official company entity details, registered addresses, branding, and contact info.',
    iconBg: 'bg-amber-500/10 text-amber-600 dark:bg-amber-400/20 dark:text-amber-400',
    hoverBorder: 'hover:border-amber-500/40 hover:shadow-amber-500/5',
    badge: 'Organization',
  },

  {
    id: 'crm-vocabulary',
    label: 'CRM Vocabulary',
    href: '/app/settings/crm-vocabulary',
    icon: Tags,
    category: 'Admin',
    categoryKey: 'admin',
    description: 'Customize opportunity deal stages, lead source labels, and organization taxonomy.',
    iconBg: 'bg-teal-500/10 text-teal-600 dark:bg-teal-400/20 dark:text-teal-400',
    hoverBorder: 'hover:border-teal-500/40 hover:shadow-teal-500/5',
    badge: 'Custom Fields',
  },
]

export const ASSETS_SECTIONS: SettingItem[] = [
  {
    id: 'all-assets',
    label: 'Asset Inventory',
    href: '/app/settings/assets?assetType=PHYSICAL',
    icon: LayoutGrid,
    category: 'Assets',
    categoryKey: 'assets',
    description: 'Every company asset: physical items (laptops, monitors, furniture) and digital files (logos, compliance documents, media kits).',
    iconBg: 'bg-sky-500/10 text-sky-600 dark:bg-sky-400/20 dark:text-sky-400',
    hoverBorder: 'hover:border-sky-500/40 hover:shadow-sky-500/5',
    badge: 'Inventory',
  },
  {
    id: 'asset-master-data',
    label: 'Asset Categories & Statuses',
    href: '/app/settings/master-data/asset-master-data',
    icon: Layers,
    category: 'Assets',
    categoryKey: 'assets',
    description: 'The categories (Laptop, Monitor, Image...) and statuses (Available, Assigned...) offered when adding or editing an asset.',
    iconBg: 'bg-cyan-600/10 text-cyan-700 dark:bg-cyan-400/20 dark:text-cyan-400',
    hoverBorder: 'hover:border-cyan-600/40 hover:shadow-cyan-600/5',
    badge: 'Setup',
  },
  {
    id: 'asset-allocation',
    label: 'Device Allocation',
    href: '/app/settings/asset-allocation',
    icon: Laptop,
    category: 'Assets',
    categoryKey: 'assets',
    description: 'Allocate laptops and other devices to employees, record accessories, and take them back. Visible to HR Admins and super admins.',
    iconBg: 'bg-teal-500/10 text-teal-600 dark:bg-teal-400/20 dark:text-teal-400',
    hoverBorder: 'hover:border-teal-500/40 hover:shadow-teal-500/5',
    badge: 'HR',
    access: 'hr',
  },
]

export const MASTER_DATA_SECTIONS: SettingItem[] = [
  {
    id: 'products-services',
    label: 'Products & Services',
    href: '/app/settings/master-data/products-services',
    icon: Package,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Product master items, inventory catalog, descriptions, and default sales pricing.',
    iconBg: 'bg-violet-500/10 text-violet-600 dark:bg-violet-400/20 dark:text-violet-400',
    hoverBorder: 'hover:border-violet-500/40 hover:shadow-violet-500/5',
    badge: 'Products',
  },
  {
    id: 'hsn-sac',
    label: 'HSN / SAC',
    href: '/app/settings/master-data/hsn-sac',
    icon: Hash,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Harmonized System and Service Accounting Codes for statutory taxation invoices.',
    iconBg: 'bg-cyan-500/10 text-cyan-600 dark:bg-cyan-400/20 dark:text-cyan-400',
    hoverBorder: 'hover:border-cyan-500/40 hover:shadow-cyan-500/5',
    badge: 'Taxation',
  },
  {
    id: 'taxes',
    label: 'Taxes / GST',
    href: '/app/settings/master-data/taxes',
    icon: Percent,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Define GST rates, IGST, CGST/SGST splits, surcharge rules, and calculation methods.',
    iconBg: 'bg-green-500/10 text-green-600 dark:bg-green-400/20 dark:text-green-400',
    hoverBorder: 'hover:border-green-500/40 hover:shadow-green-500/5',
    badge: 'Tax Rates',
  },
  {
    id: 'units',
    label: 'Units',
    href: '/app/settings/master-data/units',
    icon: Ruler,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Measurement standards (Hours, Days, Units, Sets, Months) used across all line items.',
    iconBg: 'bg-orange-500/10 text-orange-600 dark:bg-orange-400/20 dark:text-orange-400',
    hoverBorder: 'hover:border-orange-500/40 hover:shadow-orange-500/5',
    badge: 'Measurement',
  },
  {
    id: 'payment-terms',
    label: 'Payment Terms',
    href: '/app/settings/master-data/payment-terms',
    icon: CreditCard,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Preset milestone payment schedules, advance percentages, and net invoice terms.',
    iconBg: 'bg-pink-500/10 text-pink-600 dark:bg-pink-400/20 dark:text-pink-400',
    hoverBorder: 'hover:border-pink-500/40 hover:shadow-pink-500/5',
    badge: 'Finances',
  },
  {
    id: 'assumptions-exclusions',
    label: 'Assumptions & Exclusions',
    href: '/app/settings/master-data/assumptions-exclusions',
    icon: FileCheck,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Standard proposal scope boundaries, client prerequisites, and exclusion clauses.',
    iconBg: 'bg-lime-500/10 text-lime-600 dark:bg-lime-400/20 dark:text-lime-400',
    hoverBorder: 'hover:border-lime-500/40 hover:shadow-lime-500/5',
    badge: 'Scope',
  },
  {
    id: 'terms-conditions',
    label: 'Terms & Conditions',
    href: '/app/settings/master-data/terms-conditions',
    icon: FileText,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Standard legal terms, commercial stipulations, and default contract agreements.',
    iconBg: 'bg-slate-500/10 text-slate-600 dark:bg-slate-400/20 dark:text-slate-400',
    hoverBorder: 'hover:border-slate-500/40 hover:shadow-slate-500/5',
    badge: 'Legal',
  },
  {
    id: 'bank-details',
    label: 'Bank Details',
    href: '/app/settings/master-data/bank-details',
    icon: Banknote,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Corporate bank accounts, IFSC / SWIFT numbers, and wire transfer payment details.',
    iconBg: 'bg-emerald-600/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
    hoverBorder: 'hover:border-emerald-600/40 hover:shadow-emerald-600/5',
    badge: 'Banking',
  },
  {
    id: 'declarations',
    label: 'Declarations',
    href: '/app/settings/master-data/declarations',
    icon: Stamp,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Official quote and invoice declarations, authorized signatory text, and footers.',
    iconBg: 'bg-fuchsia-500/10 text-fuchsia-600 dark:bg-fuchsia-400/20 dark:text-fuchsia-400',
    hoverBorder: 'hover:border-fuchsia-500/40 hover:shadow-fuchsia-500/5',
    badge: 'Compliance',
  },
  {
    id: 'quotation-defaults',
    label: 'Quotation Defaults',
    href: '/app/settings/master-data/quotation-defaults',
    icon: Briefcase,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Default quote validity timeline, currency, discount thresholds, and standard notes.',
    iconBg: 'bg-yellow-500/10 text-yellow-600 dark:bg-yellow-400/20 dark:text-yellow-400',
    hoverBorder: 'hover:border-yellow-500/40 hover:shadow-yellow-500/5',
    badge: 'Quotations',
  },
  {
    id: 'display-settings',
    label: 'Document Display',
    href: '/app/settings/master-data/display-settings',
    icon: LayoutGrid,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Configure table columns, line item visibility, and print presentation formats.',
    iconBg: 'bg-indigo-600/10 text-indigo-700 dark:bg-indigo-400/20 dark:text-indigo-400',
    hoverBorder: 'hover:border-indigo-600/40 hover:shadow-indigo-600/5',
    badge: 'Layout',
  },
  {
    id: 'document-numbering',
    label: 'Document Numbering',
    href: '/app/settings/master-data/document-numbering',
    icon: Settings,
    category: 'Master Data',
    categoryKey: 'master-data',
    description: 'Configure auto-incrementing serial prefixes, separators, and sequence rules.',
    iconBg: 'bg-rose-600/10 text-rose-700 dark:bg-rose-400/20 dark:text-rose-400',
    hoverBorder: 'hover:border-rose-600/40 hover:shadow-rose-600/5',
    badge: 'Sequences',
  },
]

export const HR_SECTIONS: SettingItem[] = [
  {
    id: 'hr-leave-types',
    label: 'Leave Policy',
    href: '/app/settings/hr/leave-types',
    icon: CalendarRange,
    category: 'HR',
    categoryKey: 'hr',
    description: 'Leave types and yearly quotas. Every employee gets this balance each year and on joining.',
    iconBg: 'bg-blue-500/10 text-blue-600 dark:bg-blue-400/20 dark:text-blue-400',
    hoverBorder: 'hover:border-blue-500/40 hover:shadow-blue-500/5',
    badge: 'Leave',
  },
  {
    id: 'hr-holidays',
    label: 'Holiday Calendar',
    href: '/app/settings/hr/holidays',
    icon: CalendarDays,
    category: 'HR',
    categoryKey: 'hr',
    description: 'Mandatory and optional holidays for the year, and how many optional ones each person may take.',
    iconBg: 'bg-rose-500/10 text-rose-600 dark:bg-rose-400/20 dark:text-rose-400',
    hoverBorder: 'hover:border-rose-500/40 hover:shadow-rose-500/5',
    badge: 'Holidays',
  },
  {
    id: 'hr-branding',
    label: 'Letterhead Branding',
    href: '/app/settings/hr/branding',
    icon: Palette,
    category: 'HR',
    categoryKey: 'hr',
    description: 'Company identity, header and footer artwork and authorised signatory applied to every generated HR document.',
    iconBg: 'bg-fuchsia-500/10 text-fuchsia-600 dark:bg-fuchsia-400/20 dark:text-fuchsia-400',
    hoverBorder: 'hover:border-fuchsia-500/40 hover:shadow-fuchsia-500/5',
    badge: 'Letters',
  },
  {
    id: 'hr-templates',
    label: 'Document Templates',
    href: '/app/settings/hr/templates',
    icon: Copy,
    category: 'HR',
    categoryKey: 'hr',
    description: 'Offer, appointment, NDA and other HR letter templates, company-wide or per department.',
    iconBg: 'bg-indigo-500/10 text-indigo-600 dark:bg-indigo-400/20 dark:text-indigo-400',
    hoverBorder: 'hover:border-indigo-500/40 hover:shadow-indigo-500/5',
    badge: 'Letters',
  },
  {
    id: 'hr-employee-codes',
    label: 'Employee Code Series',
    href: '/app/settings/hr/employee-codes',
    icon: Hash,
    category: 'HR',
    categoryKey: 'hr',
    description: 'Department and role-based employee code prefix rules (DT-26-FD001, DT-26-HR001, etc.) and series counters.',
    iconBg: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/20 dark:text-emerald-400',
    hoverBorder: 'hover:border-emerald-500/40 hover:shadow-emerald-500/5',
    badge: 'Master Data',
  },
  {
    id: 'hr-activity-targets',
    label: 'Activity Targets',
    href: '/app/settings/hr/activity-targets',
    icon: Activity,
    category: 'HR',
    categoryKey: 'hr',
    description: 'Calls, meetings and follow-ups the sales team should get through: a company default, with targets of their own for anyone.',
    iconBg: 'bg-orange-500/10 text-orange-600 dark:bg-orange-400/20 dark:text-orange-400',
    hoverBorder: 'hover:border-orange-500/40 hover:shadow-orange-500/5',
    badge: 'Sales',
  },
]

export const ALL_SETTINGS_SECTIONS: SettingItem[] = [
  ...MY_ACCOUNT_SECTIONS,
  ...ADMIN_SECTIONS,
  ...ASSETS_SECTIONS,
  ...MASTER_DATA_SECTIONS,
  ...HR_SECTIONS,
]

export const SETTINGS_GROUPS: SettingGroup[] = [
  {
    key: 'my-account',
    label: 'My Account',
    description: 'Your personal preferences, security, and notification configurations',
    access: 'everyone',
    items: MY_ACCOUNT_SECTIONS,
  },
  {
    key: 'admin',
    label: 'Admin & Workspace',
    description: 'Manage users, teams, permissions, and organization-wide profiles',
    access: 'superadmin',
    items: ADMIN_SECTIONS,
  },
  {
    key: 'assets',
    label: 'Assets & Media',
    description: 'Centralized company media, marketing documents, and sales enablement decks',
    access: 'superadmin',
    items: ASSETS_SECTIONS,
  },
  {
    key: 'master-data',
    label: 'Master Data',
    description: 'Standard reference catalogs, tax formulas, banking info, and commercial rules',
    access: 'superadmin',
    items: MASTER_DATA_SECTIONS,
  },
  {
    key: 'hr',
    label: 'HR',
    description: 'Leave policy, holiday calendar, letterhead and letter templates: set once, used across HR',
    access: 'hr',
    items: HR_SECTIONS,
  },
]
